import { endOfDay, parse, parseJSON, startOfDay } from "date-fns";
import { Box, Button, Grid, Text } from "grommet";
import { orderBy, set } from "lodash";
import React from "react";
import { FormatDateTime } from "../components/DateTimePicker";
import SkeletonWrapper from "../components/SkeletonWrapper";
import MatchCalendar from "../matches/MatchCalendar";
import { getMatchDays, getMatches, getMatchesBetween, MatchViewModel } from "../matches/MatchesStore";
import { SmallMatchRow } from "../matches/SmallMatchRow";
import axios from "../general/HttpClient";
import { GetOrganizationInvites, OrganizationInviteViewModel } from "../registration/RegistrationStore";
import { OrganizationInvite, TournamentInvite } from "../tournaments/TournamentInvites";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LinkButton from "../components/LinkButton";

const dateFormat = "yyyy-MM-dd";

const right = <FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>;

const ManagementDashboard = () => {
    const [matchDays, setMatchDays] = React.useState<string[]>([]);

    const [selectedDay, setSelectedDay] = React.useState(FormatDateTime(new Date(), dateFormat));

    const [matchSchedulePast, setMatchSchedulePast] = React.useState([] as MatchViewModel[]);
    const [matchScheduleFuture, setMatchScheduleFuture] = React.useState([] as MatchViewModel[]);
    const [matches, setMatches] = React.useState([] as MatchViewModel[]);
    const [matchesLoading, setMatchesLoading] = React.useState(true);

    const onSelect = (day) => {
        setSelectedDay(day);
    };

    React.useEffect(() => {
        load();
        loadAnnouncements();
        loadInvites();
    }, []);

    React.useEffect(() => {
        let day = parse(selectedDay, dateFormat, new Date());
        setMatchesLoading(true);
        getMatchesBetween(startOfDay(day), endOfDay(day)).then((matches) => {
            setMatches(matches);
            setMatchesLoading(false);
        });
    }, [selectedDay]);

    const [invites, setInvites] = React.useState([] as OrganizationInviteViewModel[]);
    const [loadingInvites, setLoadingInvites] = React.useState(true);

    const loadInvites = async () => {
        GetOrganizationInvites().then((res) => {
            setInvites(res.result);
            setLoadingInvites(false);
        });
    };

    const load = async () => {
        let _matches = [] as MatchViewModel[];

        _matches = await getMatches(true, true, false, 0, 50);

        setMatchSchedulePast(
            orderBy(
                _matches.filter((m) => parseJSON(m.startDate) < new Date()),
                (m) => parseJSON(m.startDate),
                ["asc"]
            )
        );
        setMatchScheduleFuture(
            orderBy(
                _matches.filter((m) => parseJSON(m.startDate) >= new Date()),
                (m) => parseJSON(m.startDate),
                ["asc"]
            )
        );

        setMatchDays(await getMatchDays());
    };

    const [news, setNews] = React.useState(
        [] as {
            title: {
                rendered: string;
            };
        }[]
    );
    const [loadingNews, setLoadingNews] = React.useState(true);

    const loadAnnouncements = async () => {
        axios.get("https://www.rgbgaming.co.za/wp-json/wp/v2/posts", { params: { categories: 86 } }).then((res) => {
            // setNews(res.data.slice(0, 5));
            setLoadingNews(false);
        });
    };

    return (
        <Box pad={{ vertical: "medium" }} gap="small">
            <Grid columns={["440px", "3fr"]} gap="small">
                <Box>
                    <MatchCalendar matchDays={matchDays} selectedDay={selectedDay} onSelect={onSelect}></MatchCalendar>
                    <SkeletonWrapper loading={matchesLoading}>
                        <Box gap="small">
                            {matches.map((match) => (
                                <SmallMatchRow key={match.id} match={match}></SmallMatchRow>
                            ))}
                        </Box>
                    </SkeletonWrapper>
                </Box>
                <Box gap="small">
                    {invites.length > 0 && (
                        <Box background={{ color: "background", opacity: 0.7 }} round="small" pad="small">
                            <Text>Open Invites</Text>
                            <SkeletonWrapper loading={loadingInvites}>
                                {invites.map((invite, index) => (
                                    <TournamentInvite invite={invite} key={invite.id}>
                                        <Box align="end" justify="end" fill>
                                            <LinkButton label="Join" icon={right} path={`/registration/invites/${invite.id}`} reverse></LinkButton>
                                        </Box>
                                    </TournamentInvite>
                                ))}
                            </SkeletonWrapper>
                        </Box>
                    )}

                    <Box background={{ color: "background", opacity: 0.7 }} round="small" pad="small">
                        <Text>Announcements</Text>
                        <SkeletonWrapper loading={loadingNews}>
                            {news.map((item, index) => (
                                <Box key={index} pad="small">
                                    <Text>{item.title?.rendered}</Text>
                                </Box>
                            ))}
                        </SkeletonWrapper>
                    </Box>
                </Box>
            </Grid>
        </Box>
    );
};

export default ManagementDashboard;
